import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cartService, CartItem } from '../services/cartService';
import { walletService, WalletSummary } from '../services/walletService';

const API_URL = process.env.REACT_APP_API_URL || '';

interface AddressForm {
  fullName: string; phone: string; addressLine1: string;
  addressLine2: string; city: string; state: string; pincode: string;
}
const emptyAddress = (): AddressForm => ({
  fullName: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '',
});
const formatAddress = (a: AddressForm) =>
  `${a.fullName}, ${a.phone}, ${a.addressLine1}${a.addressLine2 ? ', ' + a.addressLine2 : ''}, ${a.city}, ${a.state} - ${a.pincode}`;

const Field = ({ label, value, onChange, placeholder, required = true, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; type?: string;
}) => (
  <div className="w-full">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} required={required}
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DAA520] focus:border-transparent text-sm" />
  </div>
);

const AddressSection = ({ title, form, setForm }: { title: string; form: AddressForm; setForm: (f: AddressForm) => void }) => {
  const set = (key: keyof AddressForm) => (v: string) => setForm({ ...form, [key]: v });
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-semibold mb-5">{title}</h2>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full Name" value={form.fullName} onChange={set('fullName')} placeholder="Recipient's full name" />
          <Field label="Phone Number" value={form.phone} onChange={set('phone')} placeholder="10-digit mobile number" type="tel" />
        </div>
        <Field label="Address Line 1" value={form.addressLine1} onChange={set('addressLine1')} placeholder="House / Flat no., Building, Street" />
        <Field label="Address Line 2" value={form.addressLine2} onChange={set('addressLine2')} placeholder="Area, Colony, Landmark (optional)" required={false} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="City" value={form.city} onChange={set('city')} placeholder="City" />
          <Field label="State" value={form.state} onChange={set('state')} placeholder="State" />
          <Field label="Pincode" value={form.pincode} onChange={set('pincode')} placeholder="6-digit pincode" />
        </div>
      </div>
    </div>
  );
};

const CheckoutPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const isSubmitting = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cod' | 'online'>('cod');
  const [shipping, setShipping] = useState<AddressForm>(emptyAddress());
  const [billing, setBilling] = useState<AddressForm>(emptyAddress());

  // Wallet state
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [walletUsage, setWalletUsage] = useState<'none' | 'full' | 'partial'>('none');
  const [partialAmount, setPartialAmount] = useState('');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setCartItems(cartService.getCartItems(user.id));
    setShipping(prev => ({ ...prev, fullName: user.name || '' }));
    walletService.getWallet().then(setWallet).catch(() => {});
    setLoading(false);
  }, [user, navigate]);

  const cartTotal = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const originalTotal = cartItems.reduce((s, i) => s + (i.originalPrice || i.price) * i.quantity, 0);
  const totalDiscount = originalTotal - cartTotal;

  // Wallet deduction calculation
  const walletBalance = wallet?.balance ?? 0;
  const maxWalletUsable = Math.min(walletBalance, cartTotal);
  const walletDeduction = walletUsage === 'full'
    ? maxWalletUsable
    : walletUsage === 'partial'
      ? Math.min(Math.max(0, parseFloat(partialAmount) || 0), maxWalletUsable)
      : 0;
  const amountDueOnline = Math.max(0, cartTotal - walletDeduction);

  const validate = () => {
    const a = shipping;
    if (!a.fullName.trim()) return 'Please enter recipient name';
    if (!/^\d{10}$/.test(a.phone.trim())) return 'Please enter a valid 10-digit phone number';
    if (!a.addressLine1.trim()) return 'Please enter address line 1';
    if (!a.city.trim()) return 'Please enter city';
    if (!a.state.trim()) return 'Please enter state';
    if (!/^\d{6}$/.test(a.pincode.trim())) return 'Please enter a valid 6-digit pincode';
    return null;
  };

  const handlePlaceOrder = async () => {
    const err = validate();
    if (err) { alert(err); return; }
    if (cartItems.length === 0) { alert('Your cart is empty'); return; }
    if (isSubmitting.current) return;
    isSubmitting.current = true;

    setPlacingOrder(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const shippingStr = formatAddress(shipping);
      const billingStr = useSameAddress ? shippingStr : formatAddress(billing);

      const orderRes = await fetch(`${API_URL}/api/orders/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          shippingAddress: shippingStr,
          billingAddress: billingStr,
          paymentMethod: amountDueOnline === 0 ? 'wallet' : paymentMethod,
          notes: notes.trim(),
          walletAmount: walletDeduction,
          cartItems: cartItems.map(i => ({
            productId: i.productId, quantity: i.quantity,
            price: i.price, selectedSize: i.selectedSize, selectedColor: i.selectedColor,
            productName: i.name, productImageUrl: i.imageUrl,
          })),
        }),
      });

      if (!orderRes.ok) {
        const errData = await orderRes.json();
        setError(errData.error || 'Failed to create order');
        return;
      }

      const orderData = await orderRes.json();

      // Determine if we need to go to ClickPay:
      // - amount still due after wallet deduction AND user picked online payment
      // - OR wallet was partially used and paymentMethod is online
      const needsOnlinePayment = amountDueOnline > 0 && paymentMethod === 'online';

      if (needsOnlinePayment) {
        try {
          const res = await fetch(`${API_URL}/api/payment/initiate/${orderData.orderId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          });
          const data = await res.json();
          if (res.ok && data.redirectUrl) {
            cartService.clearCart(user!.id);
            window.location.href = data.redirectUrl;
          } else {
            // Payment gateway unavailable — inform user, order still saved
            cartService.clearCart(user!.id);
            alert(`Order #${orderData.orderNumber} placed! Payment gateway is currently unavailable. You can pay via Cash on Delivery on delivery.\n\nOrder ID: ${orderData.orderNumber}`);
            navigate('/orders');
          }
        } catch {
          // Network error reaching payment gateway — order is still placed
          cartService.clearCart(user!.id);
          alert(`Order #${orderData.orderNumber} placed! Could not reach payment gateway. Please pay via Cash on Delivery.`);
          navigate('/orders');
        }
      } else {
        cartService.clearCart(user!.id);
        alert(`Order placed! Order #${orderData.orderNumber}`);
        navigate('/orders');
      }
    } catch {
      setError('Failed to place order. Please try again.');
    } finally {
      setPlacingOrder(false);
      isSubmitting.current = false;
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DAA520]" />
    </div>
  );

  if (cartItems.length === 0) return (
    <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-4">🛒</div>
        <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
        <button onClick={() => navigate('/')} className="bg-[#DAA520] text-white px-6 py-2 rounded mt-4">Continue Shopping</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <div className="py-8 px-4 md:px-20">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Checkout</h1>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">{error}</div>}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left — Forms */}
            <div className="lg:col-span-2 space-y-6">
              <AddressSection title="Shipping Address" form={shipping} setForm={setShipping} />

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Billing Address</h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={useSameAddress} onChange={e => setUseSameAddress(e.target.checked)}
                      className="rounded border-gray-300 text-[#DAA520] focus:ring-[#DAA520]" />
                    <span className="text-sm text-gray-600">Same as shipping</span>
                  </label>
                </div>
              </div>

              {!useSameAddress && <AddressSection title="Billing Address Details" form={billing} setForm={setBilling} />}

              {/* ── Wallet Section ── */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">💰 Pay with Wallet</h2>
                  {wallet && (
                    <span className="text-sm font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                      Balance: ₹{wallet.balance.toFixed(2)}
                    </span>
                  )}
                </div>

                {!wallet || wallet.balance <= 0 ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">No wallet balance. Add money to use wallet at checkout.</p>
                    <button onClick={() => navigate('/wallet?returnTo=/checkout')}
                      className="text-xs bg-[#DAA520] text-white px-3 py-1.5 rounded font-semibold hover:bg-[#c5941c] whitespace-nowrap ml-3">
                      Add Money
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[
                      { value: 'none', label: "Don't use wallet", sub: 'Pay full amount via other method' },
                      { value: 'full', label: `Use full wallet (₹${Math.min(wallet.balance, cartTotal).toFixed(2)})`, sub: walletBalance >= cartTotal ? 'Covers entire order' : `Remaining ₹${(cartTotal - walletBalance).toFixed(2)} via other method` },
                      { value: 'partial', label: 'Use partial wallet amount', sub: 'You choose how much to use' },
                    ].map(opt => (
                      <div key={opt.value} onClick={() => setWalletUsage(opt.value as any)}
                        className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                          walletUsage === opt.value ? 'border-[#DAA520] bg-[#DAA520]/5' : 'border-gray-200 hover:border-gray-300'
                        }`}>
                        <input type="radio" readOnly checked={walletUsage === opt.value} className="h-4 w-4 text-[#DAA520] mt-0.5" />
                        <div className="ml-3">
                          <p className="text-sm font-medium">{opt.label}</p>
                          <p className="text-xs text-gray-500">{opt.sub}</p>
                        </div>
                      </div>
                    ))}
                    {walletUsage === 'partial' && (
                      <div className="ml-7">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Amount to use (max ₹{maxWalletUsable.toFixed(2)})
                        </label>
                        <input type="number" value={partialAmount} onChange={e => setPartialAmount(e.target.value)}
                          max={maxWalletUsable} min={1} placeholder="Enter amount"
                          className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#DAA520] focus:outline-none" />
                      </div>
                    )}
                    {walletDeduction > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">Wallet deduction</span><span className="text-green-700 font-semibold">- ₹{walletDeduction.toFixed(2)}</span></div>
                        <div className="flex justify-between mt-1"><span className="text-gray-600">Amount due</span><span className="font-bold text-[#E91E63]">₹{amountDueOnline.toFixed(2)}</span></div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Payment method (only if amount still due) */}
              {amountDueOnline > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h2 className="text-xl font-semibold mb-5">Payment Method</h2>
                  <div className="space-y-3">
                    {/* Cash on Delivery — always available */}
                    <div onClick={() => setPaymentMethod('cod')}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer ${
                        paymentMethod === 'cod' ? 'border-[#DAA520] bg-[#DAA520]/5' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                      <input type="radio" readOnly checked={paymentMethod === 'cod'} className="h-5 w-5 text-[#DAA520]" />
                      <div className="ml-4">
                        <p className="font-medium">Cash on Delivery</p>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {walletDeduction > 0
                            ? `Pay ₹${amountDueOnline.toFixed(2)} on delivery`
                            : 'Pay when you receive your order'}
                        </p>
                      </div>
                    </div>

                    {/* Online Payment via ClickPay */}
                    <div onClick={() => setPaymentMethod('online')}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer ${
                        paymentMethod === 'online' ? 'border-[#DAA520] bg-[#DAA520]/5' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                      <input type="radio" readOnly checked={paymentMethod === 'online'} className="h-5 w-5 text-[#DAA520]" />
                      <div className="ml-4">
                        <p className="font-medium">Online Payment via ClickPay</p>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {walletDeduction > 0
                            ? `Pay remaining ₹${amountDueOnline.toFixed(2)} via UPI / Card`
                            : 'Pay via UPI / Card / Wallet'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Order Notes (Optional)</h2>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                  placeholder="Any special instructions..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DAA520] focus:border-transparent text-sm" />
              </div>
            </div>

            {/* Right — Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6">
                <h2 className="text-xl font-semibold mb-5">Order Summary</h2>

                <div className="space-y-3 mb-5 max-h-64 overflow-y-auto">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex gap-3 items-center">
                      <img src={encodeURI(item.imageUrl)} alt={item.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0 bg-gray-100" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity} × ₹{item.price}</p>
                      </div>
                      <p className="text-sm font-semibold">₹{(item.price * item.quantity).toFixed(0)}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 mb-5 text-sm">
                  <div className="flex justify-between text-gray-500">
                    <span>MRP Total</span><span className="line-through">₹{originalTotal.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-green-600 font-medium">
                    <span>Festive Discount</span><span>- ₹{totalDiscount.toFixed(0)}</span>
                  </div>
                  {walletDeduction > 0 && (
                    <div className="flex justify-between text-purple-600 font-medium">
                      <span>💰 Wallet</span><span>- ₹{walletDeduction.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>₹0</span></div>
                  <div className="border-t pt-2 flex justify-between text-base font-bold">
                    <span>Payable Amount</span>
                    <span className="text-[#E91E63]">₹{amountDueOnline.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-400 italic">✓ All taxes included</p>
                </div>

                <button onClick={handlePlaceOrder} disabled={placingOrder}
                  className="w-full bg-[#E91E63] hover:bg-[#D81B60] text-white py-3 rounded-lg font-semibold mb-3 disabled:opacity-50 disabled:cursor-not-allowed">
                  {placingOrder ? 'Placing Order...' :
                    amountDueOnline === 0 ? 'Place Order (Wallet)' :
                    paymentMethod === 'online' ? `Pay ₹${amountDueOnline.toFixed(2)} via ClickPay` :
                    `Place Order - COD (₹${amountDueOnline.toFixed(2)})`}
                </button>
                <button onClick={() => navigate('/cart')} className="w-full border border-[#DAA520] text-[#DAA520] py-3 rounded-lg font-semibold hover:bg-[#DAA520]/10">
                  Back to Cart
                </button>
                <div className="mt-4 text-xs text-gray-400 space-y-1">
                  <p>✓ Secure checkout</p>
                  <p>✓ 7-day return policy</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
